/**
 * WelcomeForm - Asks for name + phone before starting conversation
 */
import { useState } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

export default function WelcomeForm({ onSubmit }) {
  const theme = useTheme();
  const p = theme.palette.primary.main;
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = () => {
    if (!name.trim()) return setError("Please enter your name");
    if (!phone.trim() || phone.trim().length < 10)
      return setError("Please enter a valid phone number");

    // Check for duplicate phone in existing chatbot users
    const existing = JSON.parse(localStorage.getItem("ara_chatbot_users") || "[]");
    const duplicate = existing.find((u) => u.phone === phone.trim());
    if (duplicate) {
      return setError(`This phone number is already registered by "${duplicate.name}". Please use a different number.`);
    }

    setError("");
    onSubmit({ name: name.trim(), phone: phone.trim() });
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        py: 3,
        bgcolor: "#f9fafb",
      }}
    >
      {/* Bot icon */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "16px",
          background: `linear-gradient(135deg, ${alpha(p, 0.15)}, ${alpha(p, 0.05)})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
        }}
      >
        <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
          <rect x="6" y="12" width="28" height="20" rx="6" fill={p} fillOpacity="0.2" />
          <rect x="12" y="6" width="16" height="8" rx="4" fill={p} fillOpacity="0.15" />
          <circle cx="15" cy="22" r="2.5" fill={p} />
          <circle cx="25" cy="22" r="2.5" fill={p} />
          <rect x="16" y="27" width="8" height="2" rx="1" fill={p} opacity="0.4" />
        </svg>
      </Box>

      <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", mb: 0.3 }}>
        Welcome!
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#888", mb: 2.5, textAlign: "center", lineHeight: 1.5 }}>
        Please share your details to start chatting with our digital marketing assistant.
      </Typography>

      {/* Name input */}
      <Box sx={{ width: "100%", mb: 1.2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#555", mb: 0.4, pl: 0.5 }}>
          Your Name
        </Typography>
        <InputBase
          value={name}
          onChange={(e) => { setName(e.target.value); setError(""); }}
          placeholder="Enter your name"
          fullWidth
          sx={{
            fontSize: 13,
            px: 1.5,
            py: 0.9,
            borderRadius: "12px",
            bgcolor: "#fff",
            border: `1.5px solid ${alpha(p, 0.12)}`,
            transition: "all 0.2s",
            "&.Mui-focused": {
              borderColor: alpha(p, 0.5),
              boxShadow: `0 0 0 3px ${alpha(p, 0.08)}`,
            },
          }}
        />
      </Box>

      {/* Phone input */}
      <Box sx={{ width: "100%", mb: 1.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#555", mb: 0.4, pl: 0.5 }}>
          Phone Number
        </Typography>
        <InputBase
          value={phone}
          onChange={(e) => { setPhone(e.target.value.replace(/[^0-9+]/g, "")); setError(""); }}
          placeholder="Enter phone number"
          fullWidth
          inputProps={{ inputMode: "tel" }}
          sx={{
            fontSize: 13,
            px: 1.5,
            py: 0.9,
            borderRadius: "12px",
            bgcolor: "#fff",
            border: `1.5px solid ${alpha(p, 0.12)}`,
            transition: "all 0.2s",
            "&.Mui-focused": {
              borderColor: alpha(p, 0.5),
              boxShadow: `0 0 0 3px ${alpha(p, 0.08)}`,
            },
          }}
        />
      </Box>

      {/* Error */}
      {error && (
        <Typography sx={{ fontSize: 11, color: "#e53935", mb: 1, fontWeight: 500 }}>
          {error}
        </Typography>
      )}

      {/* Submit button */}
      <Box
        onClick={handleSubmit}
        sx={{
          width: "100%",
          py: 1.1,
          borderRadius: "12px",
          background: `linear-gradient(135deg, ${p}, ${alpha(p, 0.75)})`,
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          boxShadow: `0 4px 16px ${alpha(p, 0.25)}`,
          "&:hover": {
            transform: "translateY(-1px)",
            boxShadow: `0 6px 20px ${alpha(p, 0.35)}`,
          },
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
          Start Conversation
        </Typography>
      </Box>
    </Box>
  );
}
